import {UwsClient} from "./uwsclient.js";

real.realtime.uwebsocket = (function(real) {
    'use strict';

    const INTERNAL_MESSAGE_TYPE = 'internal';

    /**
     * Most of this is based on websocket.js
     */

    let
        websocketHost               = null,  // Websocket server URI
        authEndpoint                = null,  // Authorization gatekeeper on HS
        windowId                    = null,  // Window ID (part of user data)
        status                      = null,  // Websocket connection status

        reconnectingNotification    = null,  // Reconnecting websocket notification
        notificationTimeout         = 40,    // How many seconds to wait before showing the reconnecting notification
        notificationTimeoutId       = null,  // Timeout id for show reconnecting notification
        offlineNotification         = null,  // Connection down notification

        recentConnectionOpensCount  = 0,     // How many times websocket has been opened in the past 60 seconds
        resetOpensTimeoutId         = null,  // The timeout ID for resetting the recent connection opens counter

        troubleshootTimeoutId       = null,  // Timeout id for troubleshoot
        troubleshootTimeout         = 30000, // How many milliseconds system waits on page load before showing websocket troubleshoot

        clientIsConnecting          = false, // Set to true while client is connecting
        preventReconnect            = false, // Set to true when WS connection was closed for good
        /** @type {import("./uwsclient.js").UwsClient?} */
        uwsClient                   = null,  // The underlying client instance
        onOpenHandler               = null,  // onOpen handler function (passed to instance)
        onCloseHandler              = null,  // onClose handler function (passed to instance)
        onMessageHandler            = null,  // onMessage handler function (passed to instance)
        clientClosedTime            = null,  // Time when client connection was closed
        connectionRestoredLimit     = 60,    // If connection is down more than this many seconds the connection restored notification is shown
        retryCount                  = 0,     // How many times connection has tried to reconnect
        retryLimit                  = 30,    // Maximum amount of times connection tries to reconnect
        retryTimeout                = 2500,  // How many milliseconds system waits before trying to reconnect again
        retryTimeoutId              = null,  // The timeout ID of the retry to connect callback function
        // Waiters for subscribe(topic, callback). The higher realtime wrapper owns
        // the desired topic stack; this map only tracks transport-level ACKs.
        pendingSubscriptions        = new Map()
    ;

    websocketHost = document.documentElement.getAttribute('data-uwebsocket-uri');
    authEndpoint = document.documentElement.getAttribute('data-base-uri') + '/api/websocketauth';
    windowId = document.documentElement.getAttribute('data-window-id');
    status = 'offline';

    const handleWebsocketAuthenticated = () => {
        if (troubleshootTimeoutId !== null) {
            clearTimeout(troubleshootTimeoutId);
            troubleshootTimeoutId = null;
        }

        if (notificationTimeoutId !== null) {
            clearTimeout(notificationTimeoutId);
            notificationTimeoutId = null;
        }

        retryCount = 0;
        clientIsConnecting = false;

        try {
            real.realtime.setActiveConnection('uwebsocket');
        } catch(e) {
            console.error('Something went wrong:', e);
            showTroubleshoot();
        }

        if (clientClosedTime === null) {
            // Connection has not been down. No further actions needed.
            return;
        }

        // Check how long connection was down.
        const connectionDowntime = Date.now() - clientClosedTime;
        console.log('UWebsocket :: Connection was down for ' + connectionDowntime / 1000 + ' seconds');
        if (connectionDowntime < connectionRestoredLimit * 1000) {
            // Connection was down less than connectionRestoredLimit so no need
            // to show the connection restored notification.
            clientClosedTime = null;
            if (reconnectingNotification !== null) {
                real.notification.hide();
            }
            return;
        }

        // Connection was down for longer than connectionRestoredLimit.
        if (real.window.reloadAsSoonAsPossible()) {
            return;
        }

        // Time to show connection restored notification.
        clientClosedTime = null;

        let icon = 'history';
        let warningMessage = typeof real.translate === 'function' ? real.translate('layout_connection_restored') : '';
        if (real.dom.$body.data('new-ui') && typeof howspace !== 'undefined') {
            icon = 'clock-rotate-left';
            const reloadBtn = `<button class="btn btn-tertiary p-2 reload-btn">
                <i class="fa-light fa-arrows-rotate-reverse"></i>
                <span class="btn-text">${howspace.translate('layout_reload_this_page')}</span>
            </button>`;
            warningMessage = howspace.translate('layout_connection_restored') + ' ' + reloadBtn;
        }

        const connectionRestoredNotification = real.notification.show(`
            <i class="fa-light fa-${icon} mr-2" aria-hidden="true"></i>
            ${warningMessage}
            <a href="https://help.howspace.com/en/articles/272205"
               class="p-2 ml-1"
               target="_blank"
               rel="noopener noreferrer nofollow"><i class="fa-light fa-circle-info" aria-hidden="true"></i></a>
        `, 'warning', false);

        reconnectingNotification = null;

        connectionRestoredNotification.addClass('connection-restored-notification');

        connectionRestoredNotification.on('click', '.reload-btn', () => {
            connectionRestoredNotification.off('click', '.reload-btn');
            window.location.reload();
        });
    };

    const disconnect = (reason, code) => {
        if (!code) {
            code = 4500;
        }
        preventReconnect = true;
        if (uwsClient !== null) {
            uwsClient.disconnect(code);
        }
    };

    const showTroubleshoot = function() {
        if (howspace.activityTracker.isIdle()) {
            // Websocket was disconnected due to inactivity, do not show troubleshoot, activity tracker will handle this.
            return;
        }

        const websocketHelp = document.getElementById('websocket-help');
        if (websocketHelp !== null) {
            document.getElementById('websocket-help').classList.remove('display-none');
            document.getElementById('websocket-help').parentNode.childNodes.forEach(function(node) {
                if (node.id !== 'websocket-help' && node.classList) {
                    node.classList.add('display-none');
                }
            });
        }

        const newUiTroubleShootModal = document.getElementById('websocket-troubleshoot-modal');
        if (newUiTroubleShootModal !== null) {
            howspace.components.modal.show(document.getElementById('websocket-troubleshoot-modal'));
        }
    };

    const stopAllTimers = function() {
        clearTimeout(troubleshootTimeoutId);
        troubleshootTimeoutId = null;
        clearTimeout(resetOpensTimeoutId);
        resetOpensTimeoutId = null;
        clearTimeout(notificationTimeoutId);
        notificationTimeoutId = null;
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
    }

    const _arrayBufferToString = (arrayBuf) => {
        const byteArray = Array.from(new Uint8Array(arrayBuf));
        return byteArray
          .map((bytes) => bytes.toString(16).padStart(2, '0'))
          .join('');
    };

    const settlePendingSubscription = (topic, error = null, retryAfterReconnect = false) => {
        const pending = pendingSubscriptions.get(topic);
        if (!pending) {
            return;
        }

        pendingSubscriptions.delete(topic);
        pending.forEach(({callback, timeoutId}) => {
            clearTimeout(timeoutId);
            callback(topic, error, retryAfterReconnect);
        });
    };

    const rejectPendingSubscriptions = (error, retryAfterReconnect = false) => {
        pendingSubscriptions.forEach((_, topic) => {
            settlePendingSubscription(topic, error, retryAfterReconnect);
        });
    };

    const subscribeAndNotify = (topic, token, callback, timeoutMs) => {
        const pending = pendingSubscriptions.get(topic) || [];
        const shouldSubscribe = pending.length === 0;
        const timeoutId = setTimeout(() => {
            settlePendingSubscription(
                topic,
                new Error(`Timed out while subscribing to ${topic}`),
            );
        }, timeoutMs);

        pending.push({callback, timeoutId});
        pendingSubscriptions.set(topic, pending);

        if (!shouldSubscribe) {
            return;
        }

        if (token) {
            uwsClient.sub_token(topic, token);
        } else {
            uwsClient.subscribe(topic);
        }
    };

    const isInternalProtocolMessage = (event) => event?.message?.type === INTERNAL_MESSAGE_TYPE;

    const shouldLogNotificationEvent = (event) => !isInternalProtocolMessage(event);

    return {

        getStatus : function() {
            return status;
        },

        connect : function() {
            if (!websocketHost) {
                console.error('UWebsocket :: data-uwebsocket-uri attribute missing from html element.');
                real.realtime.setIsReady();
                return;
            }

            if (uwsClient !== null) {
                // Already connected.
                return;
            }

            if (clientIsConnecting === true) {
                // Establishing connection.
                return;
            }

            if (preventReconnect === true) {
                // Connection was closed for good, do not try to connect again.
                return;
            }

            if (status === 'offline') {
                troubleshootTimeoutId = setTimeout(showTroubleshoot, troubleshootTimeout);
            }

            let retryPrefix = '';
            if (status !== 'offline') {
                retryPrefix = 're-';
            }

            status = retryPrefix + 'connecting';
            clientIsConnecting = true;

            onOpenHandler = function() {
                console.log('UWebsocket :: connection opened');
                real.websocketOpened();
                status = retryPrefix + 'connected';

                recentConnectionOpensCount++;
                if (resetOpensTimeoutId !== null) {
                    clearTimeout(resetOpensTimeoutId);
                }
                resetOpensTimeoutId = setTimeout(() => {
                    // Once websocket has been open for 60 seconds we reset the recentConnectionOpensCount to zero.
                    resetOpensTimeoutId = null;
                    recentConnectionOpensCount = 0;
                }, 60 * 1000);

                handleWebsocketAuthenticated();
            };

            onCloseHandler = function(code, reason) {
                console.log('UWebsocket :: connection closed with code', code, 'reason', reason);
                status = 'disconnected';
                rejectPendingSubscriptions(
                    new Error('Websocket closed before subscription was acknowledged.'),
                    code < 4000,
                );

                const showOfflineNotification = function() {

                    real.onTierReady(real.TIER_2, function() {
                        // Could not reconnect, show connection lost notification.
                        if (notificationTimeoutId !== null) {
                            clearTimeout(notificationTimeoutId);
                            notificationTimeoutId = null;
                        }

                        let warningMessage = typeof real.translate === 'function' ? real.translate('layout_connection_offline_warning') : '';
                        if (real.dom.$body.data('new-ui') && typeof howspace !== 'undefined') {
                            const reloadBtn = `<button class="btn btn-tertiary p-2 reload-btn">
                                <i class="fa-light fa-arrows-rotate-reverse"></i>
                                <span class="btn-text">${howspace.translate('layout_reload_this_page')}</span>
                            </button>`;
                            warningMessage = howspace.translate('layout_connection_offline_warning') + ' ' + reloadBtn;
                        }

                        offlineNotification = real.notification.show(`
                            <i class="fa-light fa-ban mr-2" aria-hidden="true"></i>
                            ${warningMessage}
                            <a href="https://help.howspace.com/en/articles/272205"
                               class="p-2 ml-1"
                               target="_blank"
                               rel="noopener noreferrer nofollow"><i class="fa-light fa-circle-info" aria-hidden="true"></i></a>
                        `, 'danger', false);

                        reconnectingNotification = null;

                        offlineNotification.on('click', '.reload-btn', () => {
                            offlineNotification.off('click', '.reload-btn');
                            window.location.reload();
                        });
                    });
                };

                if (clientClosedTime === null) {
                    clientClosedTime = Date.now();
                }

                clientIsConnecting = false;
                uwsClient = null;

                real.onTierReady(real.TIER_2, function() {
                    // We prevent reconnection attempts on most of the explicit disconnect codes.
                    if (code >= 4000) {
                        if (!real.window.isUnloading()) {
                            // Do not try to re-connect if connection was closed intentionally.
                            console.log('UWebsocket :: not trying to reconnect, because code was', code);
                            preventReconnect = true;
                            showTroubleshoot();
                        }
                        return;
                    }

                    // When we're closed unexpectedly, we'll try to reconnect (e.g. deploy).
                    if (retryCount >= retryLimit) {
                        // Websocket retry limit was reached.
                        retryCount = 0;
                        console.log('UWebsocket :: connection retry limit reached, will not try to open websocket connection anymore.');
                        preventReconnect = true;
                        showOfflineNotification();
                        return;
                    }

                    if (recentConnectionOpensCount >= 5) {
                        // Websocket has been closed and opened already 5 times within the past 60 seconds.
                        // There's a good change that client is stuck in some re-connect loop. Stop here.
                        retryCount = 0;
                        console.log('UWebsocket :: connection closed and opened too frequently, will not try to open websocket connection anymore.');
                        preventReconnect = true;
                        showOfflineNotification();
                        return;
                    }

                    // Try to reconnect if websocket connection was lost or is temporarily unreachable.
                    // If this is first re-connect set the timeout to 0 ms. Otherwise use the configured timeout.
                    retryCount++;
                    retryTimeoutId = setTimeout(function() {
                        retryTimeoutId = null;
                        real.realtime.uwebsocket.connect();
                    }, retryCount === 1 ? 0 : retryTimeout);

                    // Connection was lost, start timeout to show "reconnecting" notification.
                    if (notificationTimeoutId !== null) {
                        // Show reconnecting notification timeout is already running.
                        return;
                    }

                    if (reconnectingNotification === null || reconnectingNotification.is(':hidden')) {
                        notificationTimeoutId = setTimeout(function() {
                            notificationTimeoutId = null;

                            let warningMessage = typeof real.translate === 'function' ? real.translate('layout_connection_lost_reconnecting') : '';
                            if (real.dom.$body.data('new-ui') && typeof howspace !== 'undefined') {
                                warningMessage = howspace.translate('layout_connection_lost_reconnecting');
                            }

                            reconnectingNotification = real.notification.show(`
                                <i class="fa-light fa-plug mr-2" aria-hidden="true"></i>
                                ${warningMessage}...
                                <i class="fa-light fa-spinner fa-spin ml-2" aria-hidden="true"></i>
                                <a href="https://help.howspace.com/en/articles/272205"
                                   class="p-2 ml-1"
                                   target="_blank"
                                   rel="noopener noreferrer nofollow"><i class="fa-light fa-circle-info" aria-hidden="true"></i></a>
                            `, 'warning', false);
                        }, notificationTimeout * 1000);
                    }
                });
            };

            onMessageHandler = function(event) {
                const eventType = event?.message?.notification?.action ?? 'unknown';
                if (shouldLogNotificationEvent(event)) {
                    console.debug('UWebsocket :: got event "' + eventType + '"\n', event);
                }

                real.onTierReady(real.TIER_2, () => {
                    // Check if the message was anonymous
                    const pseudo = event?.message?.notification?.actor?.pseudo;
                    if (pseudo) {
                        // For anonymous messages we need to calculate the hash using our personal
                        // anonymous ID to see if we are the author.
                        const encoded = new TextEncoder().encode(real.dom.$body.data('pseudo-id') + '|' + pseudo.challenge);
                        crypto.subtle.digest('SHA-256', encoded).then((arrayBuf) => {
                            if (_arrayBufferToString(arrayBuf) === pseudo.hash) {
                                if (shouldLogNotificationEvent(event)) {
                                    console.debug('UWebsocket :: own event');
                                }
                                // Patch in the regular actor, so code relying on real.events.isFromCurrentUser
                                // works out of the box.
                                event.message.notification.actor = {user: {id: real.dom.$body.data('user-id')}};
                            }
                            real.events.triggerServerEvents([event]);
                        });
                    } else {
                        real.events.triggerServerEvents([event]);
                    }
                });
            };

            try {
                real.websocketConnecting();
                uwsClient = new UwsClient(
                    websocketHost,
                    authEndpoint,
                    windowId,
                    onOpenHandler,
                    onCloseHandler,
                    onMessageHandler,
                    // Control messages are not app notifications. The wrapper needs
                    // the topic from sub_ok/sub_rejected to settle ACK callbacks when
                    // replaying multiple topics through one subscribe call.
                    (message) => {
                        if (message.type === 'sub_ok') {
                            settlePendingSubscription(message.topic);
                        } else if (message.type === 'sub_rejected') {
                            settlePendingSubscription(
                                message.topic,
                                new Error(`Subscription rejected for ${message.topic}`),
                            );
                        }
                    }
                );
                uwsClient.connect();
            } catch (e) {
                // NOTE: Connecting is not synchronous, we'll probably never enter this block.
                console.log('UWebsocket :: Sorry, cannot establish websocket connection :\'(');
                console.error(e);
                showTroubleshoot();
            }
        },

        disconnect : () => {
            disconnect("Using the real.realtime.uwebsocket.disconnect() interface.");
        },

        subscribe : function(topics, tokens, callback = null, timeoutMs = 15000) {
            if (uwsClient === null || clientIsConnecting) {
                // The realtime wrapper may call us while a reconnect is already in
                // progress. Keep that state transport-local instead of adding status
                // checks to the wrapper.
                if (typeof callback === 'function') {
                    const error = preventReconnect
                        ? new Error('Websocket is not connected.')
                        : new Error('Websocket is reconnecting before subscription was acknowledged.');
                    topics.forEach(topic => callback(topic, error, !preventReconnect));
                }
                return;
            }

            if (!Array.isArray(tokens)) {
                tokens = [];
            }

            topics.forEach(function(topic, idx) {
                if (typeof callback === 'function') {
                    subscribeAndNotify(topic, tokens[idx], callback, timeoutMs);
                    return;
                }

                if (tokens[idx]) {
                    uwsClient.sub_token(topic, tokens[idx]);
                } else {
                    uwsClient.subscribe(topic);
                }
            });
        },

        unsubscribe : function(topics) {
            if (uwsClient === null || clientIsConnecting) {
                // Websocket is not connected.
                return;
            }

            topics.forEach(function(topic) {
                uwsClient.unsubscribe(topic);
            });
        },

        typing : function(discussionId, commentId = null) {
            const pageId = real.dom.$body.data('page-id');
            if (!pageId || uwsClient === null || clientIsConnecting) {
                // Websocket is not connected.
                return;
            }

            uwsClient.send_typing(pageId, discussionId, commentId);
        },

        getConnectionId: () => {
            return  uwsClient?.websocket_id;
        },
    };

})(real);

// Connect to topics
real.realtime.subscribe(JSON.parse(document.documentElement.getAttribute('data-pubsub-topics')));
